import React from 'react';
import { MessageCircle, Mail, Send, Smartphone, Globe, Server } from 'lucide-react';

/**
 * Compact chip rendered next to a Service Job's Party Name showing the most
 * recent customer notification dispatch.
 *
 * Reads the denormalised `last_notify_*` fields written by the backend
 * `notify_customer` endpoint:
 *   • last_notify_at        ISO timestamp
 *   • last_notify_channel   'whatsapp' | 'email'
 *   • last_notify_mode      'wame' | 'browser' | 'api' | null (email)
 *   • last_notify_recipient 'customer' | 'supervisor' | 'technician'
 *   • last_notify_trigger   the status that triggered the send
 *   • last_notify_success   bool — last attempt succeeded
 *
 * Renders nothing if no notification has ever been dispatched.
 */

const MODE_LABEL = { wame: 'wa.me', browser: 'Web', api: 'API' };
const MODE_ICON = { wame: Smartphone, browser: Globe, api: Server };

const timeAgo = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
};

export function LastNotifyChip({ job, className = '' }) {
  if (!job?.last_notify_at) return null;

  const isWA = job.last_notify_channel === 'whatsapp';
  const ChannelIcon = isWA ? MessageCircle : Mail;
  const ModeIcon = isWA ? (MODE_ICON[job.last_notify_mode] || Send) : Mail;
  const success = job.last_notify_success !== false; // default true if missing
  const tone = success
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/60'
    : 'bg-rose-50 text-rose-700 ring-rose-200/60';

  const modeLabel = isWA ? (MODE_LABEL[job.last_notify_mode] || '') : 'Email';
  const detail = isWA && modeLabel ? `${modeLabel}` : 'Email';
  const recipient = job.last_notify_recipient || '';
  const trigger = job.last_notify_trigger || '';

  const tooltip = [
    success ? '✓ Sent' : '✗ Failed',
    detail,
    recipient && `→ ${recipient}`,
    trigger && `(trigger: ${trigger})`,
    new Date(job.last_notify_at).toLocaleString(),
  ].filter(Boolean).join(' · ');

  return (
    <span
      title={tooltip}
      data-testid={`last-notify-${job.id}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium ring-1 ${tone} ${className}`}
    >
      <ChannelIcon size={10} className="opacity-80" />
      <ModeIcon size={9} className="opacity-70" />
      <span className="tracking-tight">{detail}</span>
      <span className="opacity-50">·</span>
      <span className="opacity-80">{timeAgo(job.last_notify_at)}</span>
    </span>
  );
}

export default LastNotifyChip;
